import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabaseClient';
import { adicionarXP, desbloquearConquista } from '../../../lib/gamificacao';

interface Desafio {
  id: string;
  titulo: string;
  descricao: string;
  icone: string;
  meta: number;
  progresso: number;
  xpRecompensa: number;
  concluido: boolean;
  cor: string;
  dica: string;
  linkAcao: string;
  textoAcao: string;
}

interface DesafioSemanalProps {
  userId: string;
}

function calcularTempoRestante() {
  const agora = new Date();
  const diaSemana = agora.getDay();
  const diasParaDomingo = diaSemana === 0 ? 7 : 7 - diaSemana;
  const proximoDomingo = new Date(agora);
  proximoDomingo.setDate(agora.getDate() + diasParaDomingo);
  proximoDomingo.setHours(23, 59, 59, 0);

  const diff = proximoDomingo.getTime() - agora.getTime();
  const dias = Math.floor(diff / (1000 * 60 * 60 * 24));
  const horas = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutos = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const segundos = Math.floor((diff % (1000 * 60)) / 1000);

  return { dias, horas, minutos, segundos };
}

function getInicioSemana() {
  const agora = new Date();
  const diaSemana = agora.getDay();
  const inicio = new Date(agora);
  inicio.setDate(agora.getDate() - (diaSemana === 0 ? 6 : diaSemana - 1));
  inicio.setHours(0, 0, 0, 0);
  return inicio.toISOString();
}

const DESAFIOS_BASE: Omit<Desafio, 'progresso' | 'concluido'>[] = [
  {
    id: 'gratidoes_semana',
    titulo: 'Gratidão em Ação',
    descricao: 'Escreva 3 gratidões esta semana',
    icone: 'ri-heart-3-line',
    meta: 3,
    xpRecompensa: 75,
    cor: 'amber',
    dica: 'Abra o Caderno da Gratidão e registre pelo menos 3 coisas pelas quais você é grato esta semana.',
    linkAcao: '/gratidao',
    textoAcao: 'Ir para Gratidão',
  },
  {
    id: 'etapas_semana',
    titulo: 'Progresso nas Metas',
    descricao: 'Complete 2 etapas de uma meta',
    icone: 'ri-checkbox-circle-line',
    meta: 2,
    xpRecompensa: 60,
    cor: 'green',
    dica: 'Acesse o Diário Digital, abra uma meta e marque pelo menos 2 etapas como concluídas.',
    linkAcao: '/diario',
    textoAcao: 'Ir para o Diário',
  },
  {
    id: 'streak_semana',
    titulo: 'Sequência Poderosa',
    descricao: 'Acesse o diário 5 dias seguidos',
    icone: 'ri-fire-line',
    meta: 5,
    xpRecompensa: 100,
    cor: 'orange',
    dica: 'Entre no Diário Digital todos os dias esta semana para manter sua sequência ativa.',
    linkAcao: '/diario',
    textoAcao: 'Manter Sequência',
  },
  {
    id: 'meta_semana',
    titulo: 'Conquistador',
    descricao: 'Complete 1 meta esta semana',
    icone: 'ri-trophy-line',
    meta: 1,
    xpRecompensa: 150,
    cor: 'rose',
    dica: 'Conclua todas as etapas de uma meta no Diário Digital para marcar como concluída.',
    linkAcao: '/diario',
    textoAcao: 'Ver Minhas Metas',
  },
];

const COR_MAP: Record<string, { bg: string; border: string; text: string; bar: string; badge: string; glow: string }> = {
  amber: {
    bg: 'from-amber-500/10 to-yellow-500/10',
    border: 'border-amber-500/30',
    text: 'text-amber-400',
    bar: 'from-amber-500 to-yellow-500',
    badge: 'bg-amber-500',
    glow: 'shadow-amber-500/20',
  },
  green: {
    bg: 'from-green-500/10 to-emerald-500/10',
    border: 'border-green-500/30',
    text: 'text-green-400',
    bar: 'from-green-500 to-emerald-500',
    badge: 'bg-green-500',
    glow: 'shadow-green-500/20',
  },
  teal: {
    bg: 'from-teal-500/10 to-cyan-500/10',
    border: 'border-teal-500/30',
    text: 'text-teal-400',
    bar: 'from-teal-500 to-cyan-500',
    badge: 'bg-teal-500',
    glow: 'shadow-teal-500/20',
  },
  orange: {
    bg: 'from-orange-500/10 to-red-500/10',
    border: 'border-orange-500/30',
    text: 'text-orange-400',
    bar: 'from-orange-500 to-red-500',
    badge: 'bg-orange-500',
    glow: 'shadow-orange-500/20',
  },
  rose: {
    bg: 'from-rose-500/10 to-pink-500/10',
    border: 'border-rose-500/30',
    text: 'text-rose-400',
    bar: 'from-rose-500 to-pink-500',
    badge: 'bg-rose-500',
    glow: 'shadow-rose-500/20',
  },
};

export default function DesafioSemanal({ userId }: DesafioSemanalProps) {
  const navigate = useNavigate();
  const [desafios, setDesafios] = useState<Desafio[]>([]);
  const [tempo, setTempo] = useState(calcularTempoRestante());
  const [loading, setLoading] = useState(true);
  const [celebrando, setCelebrando] = useState<string | null>(null);
  const [bonusCompleto, setBonusCompleto] = useState(false);
  const [xpGanhoTotal, setXpGanhoTotal] = useState(0);
  const [desafiosConcluidosLocal, setDesafiosConcluidosLocal] = useState<Set<string>>(new Set());
  const [desafioAberto, setDesafioAberto] = useState<string | null>(null);

  const carregarProgresso = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const inicioSemana = getInicioSemana();

      const { count: gratidoesCount } = await supabase
        .from('gratidoes')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gte('created_at', inicioSemana);

      const { count: etapasCount } = await supabase
        .from('etapas_diario')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('concluida', true)
        .gte('updated_at', inicioSemana);

      const { data: stats } = await supabase
        .from('estatisticas_diario')
        .select('streak_atual, metas_concluidas')
        .eq('user_id', userId)
        .maybeSingle();

      const { count: metasCount } = await supabase
        .from('metas_diario')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('status', 'concluida')
        .gte('updated_at', inicioSemana);

      const chave = `desafios_semana_${userId}_${inicioSemana.split('T')[0]}`;
      const salvo = localStorage.getItem(chave);
      const concluidosSet: Set<string> = salvo ? new Set(JSON.parse(salvo)) : new Set();
      setDesafiosConcluidosLocal(concluidosSet);

      const progressoMap: Record<string, number> = {
        gratidoes_semana: gratidoesCount || 0,
        etapas_semana: etapasCount || 0,
        streak_semana: stats?.streak_atual || 0,
        meta_semana: metasCount || 0,
      };

      const desafiosComProgresso: Desafio[] = DESAFIOS_BASE.map((d) => ({
        ...d,
        progresso: Math.min(progressoMap[d.id] || 0, d.meta),
        concluido: concluidosSet.has(d.id) || (progressoMap[d.id] || 0) >= d.meta,
      }));

      setDesafios(desafiosComProgresso);
    } catch (err) {
      console.error('Erro ao carregar desafios:', err);
      // Carregar com progresso zero em caso de erro
      const desafiosVazios: Desafio[] = DESAFIOS_BASE.map((d) => ({
        ...d,
        progresso: 0,
        concluido: false,
      }));
      setDesafios(desafiosVazios);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    carregarProgresso();
  }, [carregarProgresso]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTempo(calcularTempoRestante());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const resgatarRecompensa = async (desafio: Desafio, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!desafio.concluido || desafiosConcluidosLocal.has(desafio.id)) return;

    setCelebrando(desafio.id);

    await adicionarXP(userId, desafio.xpRecompensa);
    setXpGanhoTotal((prev) => prev + desafio.xpRecompensa);

    const inicioSemana = getInicioSemana();
    const chave = `desafios_semana_${userId}_${inicioSemana.split('T')[0]}`;
    const novoSet = new Set(desafiosConcluidosLocal);
    novoSet.add(desafio.id);
    setDesafiosConcluidosLocal(novoSet);
    localStorage.setItem(chave, JSON.stringify(Array.from(novoSet)));

    const totalConcluidos = novoSet.size;
    if (totalConcluidos === 1) {
      await desbloquearConquista(userId, 'primeiro_desafio_semanal');
    }
    if (totalConcluidos === 5) {
      await desbloquearConquista(userId, 'cinco_desafios_semanais');
    }

    if (novoSet.size === DESAFIOS_BASE.length) {
      await adicionarXP(userId, 200);
      await desbloquearConquista(userId, 'mestre_desafios_semanais');
      setBonusCompleto(true);
    }

    setTimeout(() => setCelebrando(null), 2000);
    await carregarProgresso();
  };

  const toggleDesafio = (id: string) => {
    setDesafioAberto((prev) => (prev === id ? null : id));
  };

  const totalConcluidos = desafios.filter((d) => desafiosConcluidosLocal.has(d.id)).length;
  const progressoGeral = Math.round((totalConcluidos / DESAFIOS_BASE.length) * 100);

  if (loading) {
    return (
      <div className="mb-10 rounded-2xl bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-white/10 p-6 animate-pulse">
        <div className="h-6 bg-white/10 rounded w-48 mb-4"></div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-32 bg-white/5 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-10">
      {/* Header */}
      <div className="relative rounded-2xl overflow-hidden mb-6 bg-gradient-to-r from-slate-800 via-purple-900/40 to-slate-800 border border-purple-500/20 p-6">
        <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-amber-500/5 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/30 flex-shrink-0">
              <i className="ri-sword-line text-4xl text-white"></i>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-base font-bold tracking-widest uppercase text-purple-400 bg-purple-500/10 border border-purple-500/30 px-3 py-0.5 rounded-full">
                  ⚡ Desafio da Semana
                </span>
              </div>
              <h2 className="text-white text-3xl font-bold">Complete os desafios e ganhe XP bônus!</h2>
              <p className="text-white/50 text-lg mt-0.5">
                {totalConcluidos}/{DESAFIOS_BASE.length} desafios concluídos
                {totalConcluidos === DESAFIOS_BASE.length && (
                  <span className="ml-2 text-amber-400 font-semibold">🏆 Semana Perfeita!</span>
                )}
              </p>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-1 text-white/60 text-lg">
              <i className="ri-time-line text-purple-400"></i>
              <span>Renova em:</span>
              <span className="font-mono font-bold text-white text-xl">
                {tempo.dias > 0 && `${tempo.dias}d `}
                {String(tempo.horas).padStart(2, '0')}:{String(tempo.minutos).padStart(2, '0')}:{String(tempo.segundos).padStart(2, '0')}
              </span>
            </div>

            <div className="w-48">
              <div className="flex justify-between text-base text-white/40 mb-1">
                <span>Progresso geral</span>
                <span className="text-purple-400 font-bold">{progressoGeral}%</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-700"
                  style={{ width: `${progressoGeral}%` }}
                ></div>
              </div>
            </div>

            {xpGanhoTotal > 0 && (
              <div className="text-amber-400 text-lg font-bold animate-bounce">
                +{xpGanhoTotal} XP ganhos esta sessão! ✨
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bônus completo */}
      {bonusCompleto && (
        <div className="mb-6 rounded-2xl bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/40 p-4 flex items-center gap-4 animate-pulse">
          <div className="w-12 h-12 flex items-center justify-center text-3xl">🏆</div>
          <div>
            <p className="text-amber-400 font-bold text-2xl">Bônus Semanal Completo!</p>
            <p className="text-white/70 text-lg">Você completou todos os desafios e ganhou +200 XP bônus + conquista especial!</p>
          </div>
        </div>
      )}

      {/* Cards dos desafios */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {desafios.map((desafio) => {
          const cores = COR_MAP[desafio.cor];
          const jaResgatado = desafiosConcluidosLocal.has(desafio.id);
          const podeResgatar = desafio.concluido && !jaResgatado;
          const estaCelebrando = celebrando === desafio.id;
          const percentual = Math.round((desafio.progresso / desafio.meta) * 100);
          const estaAberto = desafioAberto === desafio.id;

          return (
            <div
              key={desafio.id}
              className={`relative rounded-2xl border transition-all duration-300 bg-gradient-to-br ${cores.bg} ${cores.border} cursor-pointer
                ${jaResgatado ? 'opacity-80' : 'hover:scale-105 hover:shadow-xl ' + cores.glow}
                ${estaCelebrando ? 'scale-105 ring-2 ring-white/30' : ''}
                ${estaAberto ? 'ring-2 ' + cores.border + ' shadow-xl ' + cores.glow : ''}
              `}
              onClick={() => toggleDesafio(desafio.id)}
            >
              {/* Badge de concluído */}
              {jaResgatado && (
                <div className="absolute -top-2 -right-2 w-7 h-7 bg-green-500 rounded-full flex items-center justify-center shadow-lg z-10">
                  <i className="ri-check-line text-white text-lg"></i>
                </div>
              )}

              {/* Celebração overlay */}
              {estaCelebrando && (
                <div className="absolute inset-0 rounded-2xl flex items-center justify-center bg-black/40 z-20">
                  <div className="text-center">
                    <div className="text-4xl mb-1">🎉</div>
                    <p className="text-white font-bold text-lg">+{desafio.xpRecompensa} XP!</p>
                  </div>
                </div>
              )}

              {/* Conteúdo principal */}
              <div className="p-4">
                {/* Ícone + seta */}
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br ${cores.bar} shadow-md`}>
                    <i className={`${desafio.icone} text-2xl text-white`}></i>
                  </div>
                  <i className={`ri-arrow-${estaAberto ? 'up' : 'down'}-s-line text-xl text-white/40 mt-1`}></i>
                </div>

                <h4 className="text-white font-bold text-lg mb-1 leading-tight">{desafio.titulo}</h4>
                <p className="text-white/50 text-sm mb-3 leading-relaxed">{desafio.descricao}</p>

                {/* Progresso */}
                <div className="mb-3">
                  <div className="flex justify-between text-sm mb-1">
                    <span className={`${cores.text} font-semibold`}>
                      {desafio.progresso}/{desafio.meta}
                    </span>
                    <span className="text-white/40">{percentual}%</span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${cores.bar} rounded-full transition-all duration-700`}
                      style={{ width: `${percentual}%` }}
                    ></div>
                  </div>
                </div>

                {/* Recompensa e botão */}
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-bold ${cores.text}`}>+{desafio.xpRecompensa} XP</span>

                  {jaResgatado ? (
                    <span className="text-sm text-green-400 font-semibold flex items-center gap-1">
                      <i className="ri-check-double-line"></i> Resgatado
                    </span>
                  ) : podeResgatar ? (
                    <button
                      onClick={(e) => resgatarRecompensa(desafio, e)}
                      className={`text-sm font-bold px-3 py-1.5 rounded-lg bg-gradient-to-r ${cores.bar} text-white cursor-pointer whitespace-nowrap hover:opacity-90 transition-opacity animate-pulse`}
                    >
                      Resgatar!
                    </button>
                  ) : (
                    <span className="text-sm text-white/30 font-medium">
                      {desafio.meta - desafio.progresso} restante{desafio.meta - desafio.progresso !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>

              {/* Painel expandido */}
              {estaAberto && (
                <div
                  className={`border-t ${cores.border} px-4 pb-4 pt-3`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className={`rounded-xl bg-white/5 p-3 mb-3`}>
                    <p className="text-white/60 text-sm leading-relaxed">
                      <i className={`ri-lightbulb-line ${cores.text} mr-1`}></i>
                      {desafio.dica}
                    </p>
                  </div>

                  <button
                    onClick={() => navigate(desafio.linkAcao)}
                    className={`w-full py-2.5 rounded-xl bg-gradient-to-r ${cores.bar} text-white font-bold text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap hover:opacity-90 transition-opacity`}
                  >
                    <i className="ri-arrow-right-circle-line text-lg"></i>
                    {desafio.textoAcao}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Rodapé */}
      <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-white/30">
        <span className="flex items-center gap-1">
          <i className="ri-information-line"></i>
          Clique em um desafio para ver como completá-lo
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-gift-line text-amber-400/50"></i>
          Complete todos os 5 para ganhar +200 XP bônus
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-refresh-line text-purple-400/50"></i>
          Os desafios renovam toda segunda-feira
        </span>
      </div>
    </div>
  );
}
